import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Row } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  isScanLabelInFlight,
  useAppContext,
  useToast,
  useTranslator,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { withForcedAiError } from '../helpers';
import {
  AddUserProductFormValues,
  isScanEntrySettled,
  markScanEntrySettled,
  SCAN_LABEL_RESULT_EVENT,
  SCAN_LABEL_RESULT_STORAGE_KEY,
} from '../AddUserProduct/helpers';

export interface ScanLabelAnalyzingState {
  formValues?: AddUserProductFormValues;
  fromAutoScan?: boolean;
  fromUserMeals?: boolean;
}

/**
 * The pair of photos being analysed, held OUTSIDE the router.
 *
 * `File` is not structured-cloneable into `history.state`, so anything put in `location.state` is
 * gone the moment the user navigates back to this route — the browser restores the entry with an
 * empty state (observed: `key: 'default'`, `state: {}`). Carrying the photos there made back from the
 * add-product form look like "no photos", which bounced the user out to the camera.
 *
 * A module-level holder survives that round trip, and is replaced wholesale by the next scan.
 */
let pendingPhotos: { front: File; nutrition: File } | null = null;

export const setPendingScanLabelPhotos = (front: File, nutrition: File) => {
  pendingPhotos = { front, nutrition };
};

// Exported for tests, which need to reproduce the "arrived here with no photos" case (a reload or a
// directly-opened URL) now that the photos no longer live in route state.
export const clearPendingScanLabelPhotos = () => {
  pendingPhotos = null;
};

/**
 * The front-of-product photo from the most recent scan, or null.
 *
 * Used by the add-product form to set it as the image of the meal it creates in the autoScan flow.
 * Deliberately NOT cleared when the analysis finishes: the form needs it after navigating away.
 */
export const getScannedFrontPhoto = () => pendingPhotos?.front ?? null;

interface TrackerScanLabelAnalyzingContainerProps {
  scanLabel: (images: File[], userComment?: string) => Promise<unknown>;
  cancelScanLabel: () => void;
}

type ScanError = 'noFood' | 'rateLimit' | 'aiAccessDenied' | 'generic';

/**
 * Analysis step of the label scanner, on its own route (`…/scan-label/analyzing`).
 *
 * Split out of the scanner for the same reason scan plate is split into `/scan-plate` and
 * `/scan-plate/details`: with the camera and the analysis on separate routes, going back during the
 * analysis is plain router history — it pops this entry and reveals the camera underneath, with no
 * popstate interception. The two photos travel here in `location.state`, mirroring how
 * ScanPlateContainer hands its single capture to ScanPlateDetailsContainer.
 */
const TrackerScanLabelAnalyzingContainer: React.FC<TrackerScanLabelAnalyzingContainerProps> = ({
  scanLabel,
  cancelScanLabel,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as ScanLabelAnalyzingState | null) ?? {};
  const { formValues, fromAutoScan = false, fromUserMeals = false } = state;
  const { toastError } = useToast();

  const {
    images: { aiStarLightIcon, doneIcon },
  } = useAppContext();

  const [scanError, setScanError] = useState<ScanError | null>(null);
  // Whether THIS history entry's analysis has already run to completion. Read once, at mount: a
  // remount of an already-settled entry (back onto an older analysis screen, whose instance
  // SlideRoutes had dropped) must open in the idle state instead of firing a fresh paid request.
  const entryAlreadySettled = useRef(isScanEntrySettled(location.key)).current;
  // False once a request settles. A successful scan navigates away, but the user can come BACK to
  // this screen (autoScan pushes the form on top of it) to re-analyse the same pair — at which point
  // there is nothing in flight, so the loader must not be spinning and the actions must be offered.
  const [isAnalyzing, setIsAnalyzing] = useState(!entryAlreadySettled);
  const [thumbUrls, setThumbUrls] = useState<{ front: string | null; nutrition: string | null }>({
    front: null,
    nutrition: null,
  });

  // `File` objects do not survive history serialisation, so a reload or a directly-opened URL lands
  // here with no photos. Held in refs (not state) because "Analyze again" resends the very same
  // pair and must not depend on a re-render.
  const frontPhotoRef = useRef<File | null>(pendingPhotos?.front ?? null);
  const nutritionPhotoRef = useRef<File | null>(pendingPhotos?.nutrition ?? null);
  const hasPhotos = Boolean(frontPhotoRef.current && nutritionPhotoRef.current);

  // Leaving mid-analysis (back gesture, TitleBar back) aborts the request, exactly like Cancel does.
  // Letting it run meant that when the response landed the success path still fired and opened the
  // add-product form on top of whatever screen the user had gone back to.
  //
  // `abandonedRef` is cleared on (re)mount, not only set on unmount: StrictMode mounts, unmounts and
  // mounts again, so a flag that only ever flips to true stayed true for the second mount and
  // swallowed the real response — the screen sat on the loader forever.
  const abandonedRef = useRef(false);
  // Set by Cancel so the resulting rejection is treated as intentional rather than as an error.
  const cancelledRef = useRef(false);
  // Read by the unmount cleanup, which must not abort a request that has already produced a result
  // (the success path navigates away, which unmounts this route).
  const isAnalyzingRef = useRef(!entryAlreadySettled);
  useEffect(() => {
    abandonedRef.current = false;
    return () => {
      abandonedRef.current = true;
    };
  }, []);

  // Leaving mid-analysis aborts the request, exactly like Cancel. Letting it run meant the success
  // path fired when the response landed and opened the add-product form on top of whatever screen the
  // user had gone back to.
  //
  // Driven by popstate, NOT by the unmount cleanup: SlideRoutes (destroy={false}) keeps this route
  // mounted when the user navigates back, so a cleanup-based abort never ran at all. popstate also
  // avoids StrictMode's throwaway unmount, which would otherwise cancel the request that had just
  // started (two cancelled ScanLabel calls and a red toast on every scan).
  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname.endsWith('/analyzing')) return;
      // Asks the connector, not this instance: SlideRoutes (destroy={false}) keeps the PREVIOUS
      // instance mounted, and that is the one whose popstate listener runs — its own
      // `isAnalyzingRef` was never set, so a per-instance check suppressed the abort every time.
      if (!isScanLabelInFlight()) return;
      abandonedRef.current = true;
      cancelScanLabel();
      // Drop any result still in storage, so the add-product form cannot prefill from work the user
      // walked away from.
      sessionStorage.removeItem(SCAN_LABEL_RESULT_STORAGE_KEY);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scan plate leaves this case as a commented-out redirect and renders an empty preview. Bounce
  // back to the camera instead: without the photos there is nothing to analyse or to show.
  //
  // Guarded on this route still being the displayed one. SlideRoutes (destroy={false}) keeps this
  // component mounted after a successful scan navigates to the add-product form, and the effect then
  // re-ran and replaced the CURRENT entry — the form's — with the scanner path. That is what made
  // back from the form land on the camera, and the next scan bounce back to it: two stray
  // `replace /scan-label` entries, confirmed in the browser's history log.
  // Reads `window.location`, NOT the router's `location`. SlideRoutes (destroy={false}) keeps this
  // component mounted after a successful scan navigates on, and its render location stays FROZEN at
  // `…/analyzing` — the same freeze the scanner's popstate listener works around. So the
  // `endsWith('/analyzing')` guard was always true, and once the autoScan path pushed the
  // add-product form this effect re-ran and replaced THAT entry with the scanner path. The user was
  // thrown off the form straight back onto the settled analysis screen, which by then reads as a
  // stuck loader offering "Analyze again" (IP-11130). `window.location` reflects where the user
  // actually is, so the guard now holds.
  useEffect(() => {
    if (!hasPhotos && window.location.pathname.endsWith('/analyzing')) {
      navigate(scannerPath(window.location.pathname), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPhotos, location.pathname]);

  // Thumbnails of the pair under analysis. Created here (not carried in route state) so this route
  // owns the blob lifetime and revokes on unmount — the previous single-component scanner had to
  // juggle revocation against SlideRoutes keeping it mounted.
  useEffect(() => {
    const front = frontPhotoRef.current;
    const nutrition = nutritionPhotoRef.current;
    if (!front || !nutrition) return undefined;
    const urls = {
      front: URL.createObjectURL(front),
      nutrition: URL.createObjectURL(nutrition),
    };
    setThumbUrls(urls);
    return () => {
      URL.revokeObjectURL(urls.front);
      URL.revokeObjectURL(urls.nutrition);
    };
  }, []);

  // A technical failure shows the app-wide red toast (Figma 9767:65957 puts it across the top),
  // unlike the content-level failures which render as a card under the photos. The error state is
  // still set so the screen offers Retake photos / Analyze again after the toast fades.
  const failGeneric = () => {
    setScanError('generic');
    toastError();
  };

  const analyze = async (front: File, nutrition: File) => {
    setScanError(null);
    setIsAnalyzing(true);
    isAnalyzingRef.current = true;
    cancelledRef.current = false;
    try {
      // withForcedAiError passes the real response through untouched unless developer options
      // pin the flow to a chosen failure (dev/stage only).
      const response: any = withForcedAiError(await scanLabel([front, nutrition]));
      if (abandonedRef.current || cancelledRef.current) return;
      // Settled — including on success, because the user can navigate BACK onto this screen and it
      // must not greet them with a spinner, nor silently re-run and bounce them forward again.
      setIsAnalyzing(false);
      isAnalyzingRef.current = false;
      // Recorded against the history entry, not just this instance: coming back here later gets a
      // brand-new instance, and only the entry-level marker can tell it the work is already done.
      markScanEntrySettled(location.key);
      if (response?.__typename === 'AnalyzedDailyTrackerIngredients') {
        const payload = {
          primaryIngredient: response.primaryIngredient,
          ...(response.requestId && { requestId: response.requestId }),
          ...(formValues && { formValues }),
          updatedAt: Date.now(),
        };
        sessionStorage.setItem(SCAN_LABEL_RESULT_STORAGE_KEY, JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent(SCAN_LABEL_RESULT_EVENT, { detail: payload }));
        // autoScan entered the scanner straight from the tracker, so there is no add-product form
        // behind it — open one (it reads the stored result on mount and prefills). The form flow
        // already has the form two entries back, so skip both the scanner and this screen.
        if (fromAutoScan) {
          // Pushed, NOT replaced: back from the form must land on this screen with both photos
          // still shown, so a bad reading can be re-analysed ("Analyze again") without shooting
          // them again. Replacing dropped this entry and sent the user back to a blank camera.
          //
          // No meal is being edited on this path, so saving the product must open a fresh meal
          // holding it (same as the standalone barcode flow) rather than dropping the user back on
          // the scanner.
          navigate(addProductPath(fromUserMeals), {
            state: {
              createMealFromProduct: true,
              aiDescriptionSource: 'scan_label',
              ...(fromUserMeals && { fromUserMeals }),
            },
          });
        } else {
          // Form flow: the form is two entries back (form -> scanner -> here). Popping both lands on
          // it, and it consumes the CustomEvent dispatched above.
          navigate(-2);
        }
      } else if (response?.__typename === 'NotFoodError') {
        setScanError('noFood');
      } else if (response?.__typename === 'RateLimitExceeded') {
        setScanError('rateLimit');
      } else if (response?.__typename === 'AiAccessDenied') {
        setScanError('aiAccessDenied');
      } else {
        failGeneric();
      }
    } catch {
      // Neither a user-initiated cancel nor a screen the user has already left should surface as a
      // technical failure. Cancel has already put the screen into its idle state.
      if (!abandonedRef.current && !cancelledRef.current) {
        setIsAnalyzing(false);
        isAnalyzingRef.current = false;
        markScanEntrySettled(location.key);
        failGeneric();
      }
    }
  };

  // Fire the analysis once per entry. Guarded by a ref rather than left to the effect's empty
  // dependency array: StrictMode runs mount effects twice, which sent the label to the paid AI
  // endpoint twice per scan. The ref survives the remount (same component instance), so the second
  // run is a no-op. A genuinely new entry gets a fresh instance, and therefore a fresh request.
  const analysisStartedRef = useRef(false);
  useEffect(() => {
    // The ref (not the empty dep array) is what makes this fire once: StrictMode runs mount effects
    // twice, which sent the label to the paid AI endpoint twice per scan.
    if (analysisStartedRef.current) return;
    // Going BACK onto an analysis entry that already finished must not re-run it: the instance is
    // new (SlideRoutes dropped the original), so the ref above is fresh and cannot tell this apart
    // from a first arrival. Re-running spent another paid AI call and, on the autoScan path, pushed
    // the add-product form again — throwing the user off the screen they navigated back to.
    // "Analyze again" remains available and bypasses this, being an explicit request.
    if (entryAlreadySettled) return;
    const front = frontPhotoRef.current;
    const nutrition = nutritionPhotoRef.current;
    if (!front || !nutrition) return;
    analysisStartedRef.current = true;
    analyze(front, nutrition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Retake photos" — discard both shots and return to step 1/2 with the camera live. Same
  // destination as the back button, so the scanner's own reset handles the state.
  const handleRetry = () => {
    navigate(-1);
  };

  // "Analyze again" — resend the photos already taken, without leaving this screen.
  const handleAnalyzeAgain = async () => {
    if (!frontPhotoRef.current || !nutritionPhotoRef.current) return;
    await analyze(frontPhotoRef.current, nutritionPhotoRef.current);
  };

  // Cancel aborts the request and STAYS here, switching to the idle state (Figma 9837:64798) where
  // both photos are still shown with "Retake photos" / "Analyze again" — IP-11084 case 3. It used to
  // navigate away, which dropped the work and landed the user on the tracker.
  const handleCancel = () => {
    // The abort makes the in-flight promise reject; flag it so the catch below does not report a
    // user-initiated cancel as a technical failure (red toast).
    cancelledRef.current = true;
    cancelScanLabel();
    setIsAnalyzing(false);
    isAnalyzingRef.current = false;
    // Cancelling settles this entry too: coming back to it must not silently restart the very
    // request the user just abandoned.
    markScanEntrySettled(location.key);
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.scanLabel.navbarTitle')}</TitleBar>

      <div className="page-template-tracker edit-meal-page">
        <Container className="py-md-4 full-height d-flex flex-column">
          <div className="tracker-container text-center d-flex flex-column flex-grow-1">
            {/* While analysing: the rotating gradient with the white disc and the status text in
                it. On failure the disc goes away and only the still gradient remains (Figma
                9767:65957) — an empty white circle read as a stuck loader. */}
            <div
              className={`voice-button ${isAnalyzing ? 'is-submitting' : ''}`}
              data-testid="scan-label-analyzing"
              data-test-id="scan_label_analyzing"
            >
              {isAnalyzing && (
                <div className="loader-container">
                  <p className="mb-0 medium">{t('tracker.scanLabel.loader.analyzing')}</p>
                </div>
              )}
            </div>

            {isAnalyzing && (
              <p className="description text-text-variant-color">
                {t('tracker.scanner.analyzing.duration')}
              </p>
            )}

            {/* mt-l/mb-l, not my-l: this project has no .my-l utility (only .my-m / .my-xl), so
                the my-l that used to be here silently did nothing. */}
            {thumbUrls.front && (
              <div className="d-flex justify-content-center gap-m mt-l mb-l">
                <div className="captured-photo-item">
                  <div className="captured-photo captured-photo-lg">
                    <img src={thumbUrls.front} alt="" />
                    <img src={doneIcon} alt="" className="captured-photo-badge" />
                  </div>
                  <p className="description mb-0">
                    {t('tracker.scanner.analyzing.imageLabel.front')}
                  </p>
                </div>
                {thumbUrls.nutrition && (
                  <div className="captured-photo-item">
                    <div className="captured-photo captured-photo-lg">
                      <img src={thumbUrls.nutrition} alt="" />
                      <img src={doneIcon} alt="" className="captured-photo-badge" />
                    </div>
                    <p className="description mb-0">
                      {t('tracker.scanner.analyzing.imageLabel.nutrition')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Content-level failures sit in the scroll area at the end of the content, the same
                placement DescribeMeal uses for its noFood/rateLimit alerts — not glued to the
                footer buttons. `generic` is not handled here: a technical failure surfaces as the
                app-wide red toast. */}
            {(scanError === 'noFood' ||
              scanError === 'rateLimit' ||
              scanError === 'aiAccessDenied') && (
              <Alert
                // Warning (amber icon) for "nothing to analyze", danger (red icon) for the AI
                // limit and access denial — both render as the DS white toast card.
                color={scanError === 'noFood' ? 'warning' : 'danger'}
                className="text-start mt-xl mb-0"
                fade={false}
                data-testid="scan-label-error"
                data-test-id="scan_label_error"
              >
                <p className="mb-0">
                  {scanError === 'noFood' && t('tracker.scanner.noLabel.notification')}
                  {scanError === 'rateLimit' && t('tracker.limitUsageError.notification')}
                  {scanError === 'aiAccessDenied' &&
                    t('tracker.scanner.aiAccessDenied.notification')}
                </p>
              </Alert>
            )}
          </div>
        </Container>

        {/* Pinned to the bottom of the viewport, matching the voice-analysis screen — inside the
            scroll container the actions sat right under the photos with barely any gap. */}
        <div className="sticky-footer">
          <Container className="px-3">
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                {!isAnalyzing ? (
                  <>
                    <Button
                      color="link"
                      className="w-100 mb-s"
                      onClick={handleRetry}
                      data-testid="scan-label-retake"
                      data-test-id="scan_label_retake"
                    >
                      {t('tracker.scanLabel.button.retakePhoto')}
                    </Button>
                    {/* Hidden when the photos did not survive the route (reload / direct URL) —
                        there would be nothing to resend. */}
                    {hasPhotos && (
                      <Button
                        color="primary"
                        className="w-100"
                        onClick={handleAnalyzeAgain}
                        data-testid="scan-label-analyze-again"
                        data-test-id="scan_label_analyze_again"
                      >
                        {/* Same white AI star the other primary "analyze" buttons use
                            (DescribeMeal / DescribeActivity), not the coloured AI badge pill —
                            Figma 9771:11064 places a 24x24 button icon here. */}
                        <img
                          src={aiStarLightIcon}
                          alt=""
                          style={{ marginTop: '-3px', marginBottom: '-3px' }}
                          className="me-2"
                        />
                        {t('tracker.scanLabel.button.analyze')}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    color="secondary"
                    outline
                    className="w-100"
                    onClick={handleCancel}
                    data-testid="scan-label-cancel"
                    data-test-id="scan_label_cancel"
                  >
                    {/* Shared with the add-product form: scan label is a step of that flow and
                        both buttons mean the same "abandon and go back". */}
                    {t('tracker.addProduct.cancelButton')}
                  </Button>
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </Layout>
  );
};

// `…/scan-label/analyzing` -> `…/scan-label`, i.e. back to the camera. Safe to derive by stripping:
// the suffix is this route's own, and says nothing about what the URL is nested under.
const scannerPath = (pathname: string) => pathname.replace(/\/analyzing$/, '');

// Where the autoScan flow opens its add-product form. Built explicitly from `fromUserMeals`, the way
// scan plate builds `scanPlateDetailsPath`, rather than by trimming segments off the current URL.
// Stripping used to work only because the scanner lived *under* the form (`/add-product/scan-label`),
// so the form's path fell out of its own; entered from an action sheet there is no form behind the
// scanner at all, and the sheet entries are now the flat `/scan-label` that the other scanners use.
const addProductPath = (fromUserMeals: boolean) =>
  fromUserMeals ? '/meals/user-meals/add-product' : '/tracker/add-product';

export default TrackerScanLabelAnalyzingContainer;
